import { ref, watch, computed } from '@vue/composition-api';
import store from '@/store';
import { title } from '@core/utils/filter';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export default function useSalesTypeList() {
  // Use toast
  const toast = useToast();

  const refSalesTypeListTable = ref(null);

  // Loading state
  const isTableLoading = ref(false);

  // Table Handlers
  const tableColumns = [
    { key: 'sno', label: 'S.No', sortable: false, thStyle: { minWidth: '80px' } },
    { key: 'id', label: 'ID', sortable: false, thStyle: { minWidth: '60px' } },
    { key: 'code', sortable: false, thStyle: { minWidth: '120px' }, stickyColumn: true },
    { key: 'name', sortable: false, thStyle: { minWidth: '200px' } },
    { key: 'prefix', sortable: false, thStyle: { minWidth: '100px' } },
    { key: 'isActive', sortable: false, thStyle: { minWidth: '120px' } },
    { key: 'actions', sortable: false, stickyColumn: true, thStyle: { minWidth: '120px' } },
  ];
  const perPage = ref(10);
  const totalSalesTypes = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');

  const dataMeta = computed(() => {
    const localItemsCount = refSalesTypeListTable.value
      ? refSalesTypeListTable.value.localItems.length
      : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalSalesTypes.value,
    };
  });

  const refetchData = () => {
    refSalesTypeListTable.value.refresh();
  };

  // Debounced search function
  const debouncedSearch = debounce(() => {
    refetchData();
  }, 500); // 500ms delay

  // Watch for page and perPage changes (immediate)
  watch([currentPage, perPage], () => {
    refetchData();
  });

  // Watch for search query changes (debounced)
  watch(searchQuery, () => {
    debouncedSearch();
  });

  const fetchSalesTypes = (ctx, callback) => {
    console.log('=== useSalesTypeList fetchSalesTypes()');

    isTableLoading.value = true;

    store
      .dispatch('fbr-invoice-sales-type/fetchSalesTypes', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
      })
      .then((response) => {
        console.log('Values ---->', response);

        const { items, pagination } = response;
        console.log('Items: -->', items);
        
        // Add serial number to each item
        const itemsWithSno = items.map((item, index) => ({
          ...item,
          sno: perPage.value * (currentPage.value - 1) + index + 1
        }));
        
        callback(itemsWithSno);
        totalSalesTypes.value = pagination.totalCount;
      })
      .catch((error) => {
        console.log('=== useSalesTypeList fetchSalesTypes() error', error);

        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching Sales Types list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      })
      .finally(() => {
        isTableLoading.value = false;
      });
  };

  return {
    fetchSalesTypes,
    tableColumns,
    perPage,
    currentPage,
    totalSalesTypes,
    dataMeta,
    perPageOptions,
    searchQuery,
    refSalesTypeListTable,

    // Loading state
    isTableLoading,

    refetchData,
  };
}
